import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, ClipboardList, Plus, Minus, FileCheck } from 'lucide-react';
import api from '../../services/api';
import { PageHeader, TableSkeleton, EmptyState, Pagination } from '../../components/PageParts';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const CATEGORIES = ['pneus', 'luzes', 'freios', 'óleo', 'água', 'documentação', 'extintor', 'tacógrafo', 'limpadores', 'bateria'];
const emptyItem = { key: '', label: '', category: 'pneus', critical: false };
const emptyTpl = { name: '', description: '', items: [] };
const emptyExec = { vehicle_id: '', template_id: '', km: '', notes: '', driver_name: '', signature_name: '', answers: [] };

function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTpl);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/checklists/templates', { params: { page, limit: 10 } });
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const openCreate = () => { setEditing(null); setForm({ ...emptyTpl, items: [] }); setModalOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...emptyTpl, ...t }); setModalOpen(true); };
  const addItem = () => setForm(f => ({ ...f, items: [...(f.items || []), { ...emptyItem, key: `item_${(f.items || []).length + 1}` }] }));
  const updItem = (i, k, v) => setForm(f => { const items = [...(f.items || [])]; items[i] = { ...items[i], [k]: v }; return { ...f, items }; });
  const rmItem = (i) => setForm(f => ({ ...f, items: (f.items || []).filter((_, x) => x !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || form.items.length === 0) { toast.error('Nome e ao menos 1 item obrigatórios'); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/checklists/templates/${editing.id}`, form);
      else await api.post('/checklists/templates', form);
      toast.success('Template salvo'); setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };
  const confirmDelete = async () => { try { await api.delete(`/checklists/templates/${deleteId}`); toast.success('Excluído'); setDeleteId(null); load(); } catch { toast.error('Erro'); } };

  return (
    <div>
      <div className="flex justify-end mb-3"><Button onClick={openCreate} className="bg-brand hover:bg-[#A81E23] text-white"><Plus className="h-4 w-4 mr-2" />Novo template</Button></div>
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
            <tr><th className="text-left px-4 py-3">Nome</th><th className="text-left px-4 py-3">Descrição</th><th className="text-right px-4 py-3">Itens</th><th className="text-right px-4 py-3">Ações</th></tr>
          </thead>
          <tbody data-testid="tpl-table" className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {loading ? <tr><td colSpan={4} className="p-4"><TableSkeleton rows={3} cols={4} /></td></tr> :
              items.length === 0 ? <tr><td colSpan={4}><EmptyState icon={ClipboardList} title="Nenhum template" /></td></tr> :
              items.map(t => (
                <tr key={t.id} className="tr-hover">
                  <td className="px-4 py-3 font-medium">{t.name}</td>
                  <td className="px-4 py-3 text-xs text-neutral-500">{t.description || '-'}</td>
                  <td className="px-4 py-3 text-right text-xs">{(t.items || []).length}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-red-600" onClick={() => setDeleteId(t.id)}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <Pagination page={page} total={total} limit={10} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Novo'} template</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5"><Label>Nome *</Label><Input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Descrição</Label><Textarea rows={2} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div className="flex items-center justify-between"><Label>Itens do checklist</Label><Button type="button" size="sm" variant="outline" onClick={addItem}><Plus className="h-3.5 w-3.5 mr-1" />Item</Button></div>
            <div className="space-y-2">
              {(form.items || []).map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-3"><Input placeholder="Chave" value={it.key} onChange={e => updItem(i, 'key', e.target.value)} className="h-9" /></div>
                  <div className="col-span-4"><Input placeholder="Rótulo" value={it.label} onChange={e => updItem(i, 'label', e.target.value)} className="h-9" /></div>
                  <div className="col-span-3">
                    <Select value={it.category || 'pneus'} onValueChange={v => updItem(i, 'category', v)}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1 flex items-center gap-1 text-xs"><input type="checkbox" checked={!!it.critical} onChange={e => updItem(i, 'critical', e.target.checked)} />Crítico</div>
                  <div className="col-span-1"><Button type="button" size="icon" variant="ghost" className="text-red-600 h-9 w-9" onClick={() => rmItem(i)}><Minus className="h-4 w-4" /></Button></div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={o => !o && setDeleteId(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Excluir template?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ExecutionsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyExec);
  const [templates, setTemplates] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/checklists/executions', { params: { page, limit: 10 } });
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => {
    (async () => {
      const [t, v] = await Promise.all([api.get('/checklists/templates', { params: { limit: 100 } }), api.get('/vehicles', { params: { limit: 200 } })]);
      setTemplates(t.data.items || []); setVehicles(v.data.items || []);
    })();
  }, []);

  const chooseTemplate = (id) => {
    const t = templates.find(x => x.id === id);
    setForm(f => ({
      ...f, template_id: id, template_name: t?.name || '',
      answers: (t?.items || []).map(item => ({ item_key: item.key, label: item.label, category: item.category, critical: !!item.critical, status: 'ok', notes: '', photo_url: '' })),
    }));
  };
  const updAnswer = (i, k, v) => setForm(f => { const answers = [...f.answers]; answers[i] = { ...answers[i], [k]: v }; return { ...f, answers }; });

  const submit = async () => {
    if (!form.vehicle_id || !form.template_id) { toast.error('Veículo e template obrigatórios'); return; }
    setSaving(true);
    try {
      const payload = { ...form, km: form.km ? Number(form.km) : null };
      const { data } = await api.post('/checklists/executions', payload);
      toast.success('Checklist registrado');
      if (data?.os_suggestion?.suggest_os) {
        setSuggestion(data.os_suggestion);
      } else {
        setModalOpen(false); setForm(emptyExec);
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={() => { setForm(emptyExec); setSuggestion(null); setModalOpen(true); }} className="bg-brand hover:bg-[#A81E23] text-white"><Plus className="h-4 w-4 mr-2" />Nova execução</Button>
      </div>
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
            <tr><th className="text-left px-4 py-3">Data</th><th className="text-left px-4 py-3">Veículo</th><th className="text-left px-4 py-3">Template</th><th className="text-left px-4 py-3">Motorista</th><th className="text-right px-4 py-3">Críticos</th></tr>
          </thead>
          <tbody data-testid="exec-table" className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {loading ? <tr><td colSpan={5} className="p-4"><TableSkeleton rows={3} cols={5} /></td></tr> :
              items.length === 0 ? <tr><td colSpan={5}><EmptyState icon={FileCheck} title="Nenhuma execução" /></td></tr> :
              items.map(e => (
                <tr key={e.id} className="tr-hover">
                  <td className="px-4 py-3 text-xs">{new Date(e.executed_at || e.created_at).toLocaleString('pt-BR')}</td>
                  <td className="px-4 py-3 font-mono text-xs">{e.vehicle_plate}</td>
                  <td className="px-4 py-3 text-xs">{e.template_name || '-'}</td>
                  <td className="px-4 py-3 text-xs">{e.driver_name || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${(e.critical_count || 0) > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{e.critical_count || 0}</span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <Pagination page={page} total={total} limit={10} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nova execução de checklist</DialogTitle></DialogHeader>
          {suggestion ? (
            <div className="space-y-3">
              <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-4">
                <p className="font-semibold text-amber-800 dark:text-amber-300">⚠ Itens críticos identificados</p>
                <p className="text-sm mt-1">{suggestion.reported_defect}</p>
                <p className="text-xs mt-2 text-neutral-600">Sugerimos gerar uma OS com prioridade <strong>{suggestion.priority}</strong>. Você pode criá-la na página de OS.</p>
              </div>
              <DialogFooter><Button onClick={() => { setModalOpen(false); setSuggestion(null); setForm(emptyExec); }}>Fechar</Button></DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Veículo *</Label>
                  <Select value={form.vehicle_id} onValueChange={v => setForm(f => ({ ...f, vehicle_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.plate}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Template *</Label>
                  <Select value={form.template_id} onValueChange={chooseTemplate}>
                    <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Motorista</Label><Input value={form.driver_name} onChange={e => setForm(f => ({ ...f, driver_name: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>KM</Label><Input type="number" value={form.km} onChange={e => setForm(f => ({ ...f, km: e.target.value }))} /></div>
                <div className="space-y-1.5 md:col-span-2"><Label>Assinatura (nome)</Label><Input value={form.signature_name} onChange={e => setForm(f => ({ ...f, signature_name: e.target.value }))} placeholder="Nome do responsável" /></div>
              </div>

              {form.answers.length > 0 && (
                <div className="space-y-2 mt-3">
                  <Label>Itens</Label>
                  {form.answers.map((a, i) => (
                    <div key={i} className={`rounded-lg border p-3 ${a.critical ? 'border-amber-300' : 'border-neutral-200 dark:border-neutral-800'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-sm">{a.label}</span>
                        {a.critical && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">CRÍTICO</span>}
                        <span className="text-[10px] text-neutral-500 ml-auto">{a.category}</span>
                      </div>
                      <div className="grid grid-cols-4 gap-1">
                        {['ok', 'warn', 'critical', 'na'].map(st => (
                          <button key={st} type="button" onClick={() => updAnswer(i, 'status', st)}
                            className={`text-xs py-1.5 rounded ${a.status === st ? (st === 'ok' ? 'bg-emerald-500 text-white' : st === 'warn' ? 'bg-amber-500 text-white' : st === 'critical' ? 'bg-red-500 text-white' : 'bg-neutral-400 text-white') : 'bg-neutral-100 dark:bg-neutral-800'}`}>
                            {st.toUpperCase()}
                          </button>
                        ))}
                      </div>
                      <Input placeholder="Observação" value={a.notes || ''} onChange={e => updAnswer(i, 'notes', e.target.value)} className="mt-2 h-8 text-xs" />
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                <Button data-testid="save-checklist" onClick={submit} disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Checklists() {
  return (
    <div>
      <PageHeader title="Checklists" subtitle="Templates configuráveis e execuções" />
      <Tabs defaultValue="executions">
        <TabsList>
          <TabsTrigger value="executions">Execuções</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="executions" className="mt-4"><ExecutionsTab /></TabsContent>
        <TabsContent value="templates" className="mt-4"><TemplatesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
